import Modal from "@/components/modal/modal";
import styles from "./StudentAffairsContactsModal.module.css";

export type StudentAffairsContact = {
  role?: string;
  name: string;
  phone?: string;
  whatsappUrl: string;
};

export type StudentAffairsContactsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  contactsLabel?: string;
  contacts: StudentAffairsContact[];
};

export default function StudentAffairsContactsModal({
  isOpen,
  onClose,
  title,
  contactsLabel,
  contacts,
}: StudentAffairsContactsModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className={styles.body}>
        <div className={styles.header}>
          <div className={styles.mainIconWrapper}>
            <svg
              width="28"
              height="28"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.698 13.4061L17.6772 13.7999L21.6109 9.86642C23.6696 7.80788 23.6657 4.45353 21.6021 2.39003C20.6053 1.39333 19.2799 0.844634 17.8687 0.844634C16.4575 0.844634 15.131 1.39333 14.1343 2.39003L9.3894 7.13471C8.19229 8.33077 7.63962 10.042 7.90905 11.7086C8.01662 12.4053 8.27518 13.0843 8.65218 13.6705C8.84759 13.9971 9.08938 14.301 9.38841 14.601L10.3131 15.5257L12.0215 13.8175L11.0967 12.8928C10.8253 12.6215 10.6181 12.3126 10.4809 11.9741C10.0437 10.9172 10.2944 9.66192 11.1056 8.85079L15.8506 4.10611C16.3904 3.56631 17.1069 3.27025 17.8668 3.27025C18.6277 3.27025 19.3432 3.5673 19.8831 4.10611C20.9953 5.21827 20.9953 7.02704 19.8831 8.13837L17.2785 10.7435L17.3121 10.8343C17.6101 11.6386 17.7433 12.5277 17.697 13.4051L17.698 13.4061Z"
                fill="var(--color-primary-500)"
              />
              <path
                d="M16.0833 12.2852C15.9896 11.6063 15.7448 10.9451 15.3579 10.3214C15.1615 9.99377 14.9178 9.68589 14.6118 9.38094L13.697 8.46613L11.9798 10.1832L12.8946 11.0981C13.1739 11.3783 13.3861 11.7 13.5095 12.0355C13.9506 13.1003 13.7059 14.3191 12.8848 15.1401L8.13981 19.8848C7.59998 20.4246 6.88446 20.7207 6.12357 20.7207C5.36267 20.7207 4.64715 20.4236 4.10733 19.8848C3.5675 19.345 3.27142 18.6286 3.27142 17.8687C3.27142 17.1078 3.56848 16.3924 4.10733 15.8526L6.71185 13.2482L6.67929 13.1574C6.38718 12.3353 6.25689 11.4482 6.30229 10.5944L6.324 10.1997L2.3903 14.1342C0.331639 16.1927 0.331639 19.543 2.3903 21.6017C3.39002 22.6014 4.72038 23.1521 6.13856 23.1521C7.55575 23.1521 8.87421 22.6044 9.86812 21.6116L14.6131 16.8669C15.8053 15.6748 16.3549 13.9616 16.0845 12.2859L16.0833 12.2852Z"
                fill="var(--color-primary-500)"
              />
            </svg>
          </div>
          <h2 className={styles.title}>{title}</h2>
        </div>

        <div className={styles.section}>
          {contactsLabel && <p className={styles.sectionLabel}>{contactsLabel}</p>}
          <div className={styles.contactsList}>
            {contacts.map((contact, index) => (
              <div key={contact.whatsappUrl || index} className={styles.contactCard}>
                <div className={styles.contactInfo}>
                  {contact.role && (
                    <div className={styles.contactRole}>
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="var(--color-primary-500)"
                        xmlns="http://www.w3.org/2000/svg"
                        className={styles.roleIcon}
                      >
                        <path d="M12 2L2 12L12 22L22 12L12 2Z" />
                      </svg>
                      <span>{contact.role}</span>
                    </div>
                  )}
                  <p className={styles.contactName}>{contact.name}</p>
                  {contact.phone && (
                    <div className={styles.contactPhoneRow}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={styles.phoneIcon}
                      >
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                      <a href={`tel:${contact.phone}`} className={styles.contactPhone}>
                        {contact.phone}
                      </a>
                    </div>
                  )}
                </div>

                <a
                  href={contact.whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  className={styles.socialIconWrapper}
                  aria-label={`Open WhatsApp chat with ${contact.name}`}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M0 24L1.687 17.837C0.645998 16.033 0.0989998 13.988 0.0999998 11.891C0.103 5.335 5.43799 0 11.993 0C15.174 0.001 18.16 1.24 20.406 3.488C22.6509 5.736 23.8869 8.724 23.8859 11.902C23.8829 18.459 18.548 23.794 11.993 23.794C10.003 23.793 8.04198 23.294 6.30499 22.346L0 24ZM6.59698 20.193C8.27298 21.188 9.87298 21.784 11.989 21.785C17.437 21.785 21.875 17.351 21.878 11.9C21.88 6.438 17.463 2.01 11.997 2.008C6.54498 2.008 2.11 6.442 2.108 11.892C2.107 14.117 2.75899 15.783 3.85399 17.526L2.85499 21.174L6.59698 20.193ZM17.984 14.729C17.91 14.605 17.712 14.531 17.414 14.382C17.117 14.233 15.656 13.514 15.383 13.415C15.111 13.316 14.913 13.266 14.714 13.564C14.516 13.861 13.946 14.531 13.773 14.729C13.6 14.927 13.426 14.952 13.129 14.803C12.832 14.654 11.874 14.341 10.739 13.328C9.85598 12.54 9.25898 11.567 9.08598 11.269C8.91298 10.972 9.06798 10.811 9.21598 10.663C9.34998 10.53 9.51298 10.316 9.66198 10.142C9.81298 9.97 9.86198 9.846 9.96198 9.647C10.061 9.449 10.012 9.275 9.93698 9.126C9.86198 8.978 9.26798 7.515 9.02098 6.92C8.77898 6.341 8.53398 6.419 8.35198 6.41L7.78198 6.4C7.58398 6.4 7.26198 6.474 6.98998 6.772C6.71798 7.07 5.94999 7.788 5.94999 9.251C5.94999 10.714 7.01498 12.127 7.16298 12.325C7.31198 12.523 9.25798 15.525 12.239 16.812C12.948 17.118 13.502 17.301 13.933 17.438C14.645 17.664 15.293 17.632 15.805 17.556C16.376 17.471 17.563 16.837 17.811 16.143C18.059 15.448 18.059 14.853 17.984 14.729Z" />
                  </svg>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
